// bulk-input-dialog-content.tsx
import { RHFTextField } from '@aibees/shared/components';
import { Box, Stack, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { MAX_VALUES } from './config';
import * as SX from './sx';
import type { AdvancedBulkInputDialogProps as Props } from './types';

export const BulkInputDialogContent = memo(
  ({
    field,
    dialogState,
    metadata,
    onValueChange,
    onMaxValuesErrorChange,
    currentValue,
  }: {
    field: string;
    dialogState: { value: boolean };
    metadata?: Props['metadata'];
    onValueChange?: (hasChanges: boolean) => void;
    onMaxValuesErrorChange?: (hasMaxValuesError: boolean) => void;
    currentValue: string;
  }) => {
    const { setValue, control } = useFormContext();
    const originalValueRef = useRef<string>('');

    const formValue = useWatch({ control, name: field });
    const { errors } = useFormState({ control, name: field });
    const hasMaxValuesError = errors[field]?.type === 'max-values';

    useEffect(() => {
      if (dialogState.value) {
        setValue(field, currentValue, { shouldDirty: false });
        originalValueRef.current = currentValue;
        onValueChange?.(false);
      }
    }, [dialogState.value, currentValue, field, setValue, onValueChange]);

    useEffect(() => {
      if (!dialogState.value) return;
      if (formValue === undefined) return;

      onValueChange?.(formValue !== originalValueRef.current);
    }, [formValue, dialogState.value, onValueChange]);

    useEffect(() => {
      onMaxValuesErrorChange?.(hasMaxValuesError);
    }, [hasMaxValuesError, onMaxValuesErrorChange]);

    const inputProps = useMemo(
      () => ({
        style: {
          overflowX: 'hidden' as const,
          width: '100%',
          boxSizing: 'border-box' as const,
        },
        'data-grammarly': 'false',
        spellCheck: false,
        autoCorrect: 'off',
        autoCapitalize: 'none',
        autoComplete: 'off',
      }),
      []
    );

    return (
      <Box sx={SX.dialogContent}>
        {metadata?.dialog?.preTextAreaComponent}
        <Stack direction="column" gap={0.5}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body5" color="grey.500" fontWeight={500}>
              Paste {metadata?.dialog?.subTitleItemLabel} (one per comma)
            </Typography>
            <Typography variant="body5" color="grey.500" fontWeight={500}>
              Max: {MAX_VALUES} {metadata?.dialog?.subTitleItemLabelPlural}
            </Typography>
          </Stack>

          <RHFTextField
            sx={SX.descriptionInput}
            name={field}
            multiline
            minRows={6}
            maxRows={6}
            placeholder={metadata?.dialog?.textArea?.placeholder}
            fullWidth
            inputProps={inputProps}
          />
        </Stack>
        {metadata?.dialog?.postTextAreaComponent}
      </Box>
    );
  }
);

BulkInputDialogContent.displayName = 'BulkInputDialogContent';
